import * as app_consts from "../../constants/react_app_consts";
import { findModuleAction } from "../../utils/functionalUtils";
import { getMenuData } from "../menu/MenuData";

const DEFAULT_CATEGORY = "Quick Links";

const normalizeSearchValue = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/[_/.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const getStoredSearchAccountActions = () => {
  if (!localStorage.getItem("log_cred")) {
    return [];
  }

  try {
    return JSON.parse(localStorage.getItem("log_cred"))?.user_actions || [];
  } catch (error) {
    console.error("Failed to parse search account actions.", error);
    return [];
  }
};

const findModule = (accountActions, moduleName) =>
  accountActions.find((module) => module?.module_name === moduleName);

const hasModule = (accountActions, moduleName) => Boolean(findModule(accountActions, moduleName));

const hasModuleAction = (accountActions, moduleName, actionName) =>
  Boolean(findModuleAction(actionName, findModule(accountActions, moduleName)));

const flattenMenuItems = (menuItems) => {
  const flatItems = [];
  let currentHeading = DEFAULT_CATEGORY;

  const walkItems = (items, parentText = "") => {
    items.forEach((item) => {
      if (item.heading) {
        currentHeading = item.heading;
        return;
      }

      if (item.text && item.link) {
        flatItems.push({
          label: item.text,
          link: item.link,
          icon: item.icon || "search",
          category: currentHeading,
          parent: parentText,
          description: parentText ? `${parentText} page` : currentHeading,
          keywords: [currentHeading, parentText].filter(Boolean),
        });
      }

      if (Array.isArray(item.subMenu) && item.subMenu.length > 0) {
        walkItems(item.subMenu, item.text || parentText);
      }
    });
  };

  walkItems(menuItems);

  return flatItems;
};

const EXTRA_SEARCH_ITEMS = [
  {
    label: "Create Goods Received",
    link: "/dashboard/goods_received/create_physical_goods_received_ticket",
    icon: "plus",
    category: "Warehouse Management",
    parent: "Goods Received",
    description: "Create a physical goods received ticket",
    keywords: ["grn", "receive goods", "warehouse", "ticket", "physical"],
    isVisible: (accountActions) =>
      hasModule(accountActions, app_consts.WAREHOUSE_MANAGEMENT),
  },
  {
    label: "Create Delivery Note",
    link: "/dashboard/delivery_notes/create_physical_delivery_note_ticket",
    icon: "plus",
    category: "Warehouse Management",
    parent: "Delivery Notes",
    description: "Create a physical delivery note ticket",
    keywords: ["dn", "dispatch", "warehouse", "ticket", "physical"],
    isVisible: (accountActions) =>
      hasModule(accountActions, app_consts.WAREHOUSE_MANAGEMENT),
  },
  {
    label: "Create Loading Order",
    link: "/dashboard/loading_orders/create",
    icon: "plus",
    category: "Warehouse Management",
    parent: "Loading Orders",
    description: "Create a loading order",
    keywords: ["load", "dispatch", "order", "warehouse"],
    isVisible: (accountActions) =>
      hasModuleAction(accountActions, app_consts.WAREHOUSE_MANAGEMENT, app_consts.CREATE_LOADING_ORDER),
  },
  {
    label: "Create Bulk Loading Order",
    link: "/dashboard/loading_orders/create_bulk",
    icon: "plus",
    category: "Warehouse Management",
    parent: "Loading Orders",
    description: "Create a bulk loading order",
    keywords: ["bulk", "load", "dispatch", "order", "warehouse"],
    isVisible: (accountActions) =>
      hasModuleAction(accountActions, app_consts.WAREHOUSE_MANAGEMENT, app_consts.CREATE_LOADING_ORDER),
  },
  {
    label: "Create User",
    link: "/dashboard/onboarding/user/create_user",
    icon: "user-add",
    category: "OnBoarding",
    parent: "User Onboarding",
    description: "Register a new dashboard user",
    keywords: ["onboard", "employee", "registration", "account"],
    isVisible: (accountActions) =>
      hasModuleAction(accountActions, app_consts.ONBOARDING, app_consts.ONBOARD_USER),
  },
  {
    label: "Smart Invoice Config",
    link: "/dashboard/smart_invoices/config",
    icon: "setting-alt",
    category: "Warehouse Management",
    parent: "Smart Invoices",
    description: "Configure smart invoice settings",
    keywords: ["settings", "invoice", "configuration", "smart invoice"],
    isVisible: (accountActions) =>
      hasModule(accountActions, app_consts.WAREHOUSE_MANAGEMENT),
  },
  {
    label: "Add Branch Location",
    link: "/dashboard/admin/branch_locations/add",
    icon: "plus",
    category: "Admin Panel",
    parent: "Branch Locations",
    description: "Add a new branch location",
    keywords: ["branch", "location", "admin", "site"],
    isVisible: (accountActions) =>
      hasModuleAction(accountActions, app_consts.SYSTEM_MANAGEMENT, app_consts.ADD_BRANCH_LOCATION),
  },
];

export const buildGlobalSearchItems = (accountActions = getStoredSearchAccountActions()) => {
  const menuItems = flattenMenuItems(
    getMenuData({
      accountActions,
      strictPermissions: true,
    })
  );
  const extraItems = EXTRA_SEARCH_ITEMS.filter((item) => item.isVisible(accountActions));
  const seenItems = new Set();

  return [...menuItems, ...extraItems].reduce((accumulator, item, index) => {
    const itemKey = `${item.label}::${item.link}`;

    if (seenItems.has(itemKey)) {
      return accumulator;
    }

    seenItems.add(itemKey);

    const searchFields = [
      item.label,
      item.category,
      item.parent,
      item.description,
      ...(item.keywords || []),
      item.link,
    ];

    accumulator.push({
      ...item,
      order: index,
      normalizedLabel: normalizeSearchValue(item.label),
      normalizedSearchText: normalizeSearchValue(searchFields.join(" ")),
    });

    return accumulator;
  }, []);
};

export const filterGlobalSearchItems = (query, searchItems) => {
  const normalizedQuery = normalizeSearchValue(query);

  if (!normalizedQuery) {
    return searchItems.slice(0, 8);
  }

  const tokens = normalizedQuery.split(" ").filter(Boolean);

  return searchItems
    .map((item) => {
      let score = 0;

      for (const token of tokens) {
        if (item.normalizedLabel === token) {
          score += 160;
          continue;
        }

        if (item.normalizedLabel.startsWith(token)) {
          score += 120;
          continue;
        }

        if (item.normalizedLabel.includes(token)) {
          score += 80;
          continue;
        }

        if (item.normalizedSearchText.includes(token)) {
          score += 35;
          continue;
        }

        return null;
      }

      return {
        ...item,
        score,
      };
    })
    .filter(Boolean)
    .sort((itemA, itemB) => itemB.score - itemA.score || itemA.order - itemB.order)
    .slice(0, 8);
};
